import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_syndic-app-1/artifacts/1ku8mjse_Logo%20Syndicappli_White_2022.png";
const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [debugToken, setDebugToken] = useState(''); // For demo only

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSuccess(true);
      // For demo purposes - remove in production
      if (response.data.debug_token) {
        setDebugToken(response.data.debug_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #00D3A8 0%, #00A88D 100%)' }}
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-5xl flex flex-col items-center">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src={LOGO_URL} 
            alt="SyndicAppli" 
            className="h-12 w-auto mx-auto"
          />
          <p className="text-white/80 text-sm mt-2 font-medium">La Vie de votre Copro</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm" data-testid="forgot-password-card">
          {success ? (
            // Success State
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-500" size={32} />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-3">Email envoyé !</h2>
              <p className="text-slate-600 text-sm mb-6">
                Un email vient de vous être envoyé afin de réinitialiser votre mot de passe.
              </p>
              
              {/* Demo: Show reset link */}
              {debugToken && (
                <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700 mb-2">🔧 Mode démo - Lien de réinitialisation :</p>
                  <Link 
                    to={`/reset-password?token=${debugToken}`}
                    className="text-[#00D3A8] hover:underline text-sm break-all"
                  >
                    Cliquez ici pour réinitialiser
                  </Link>
                </div>
              )}

              <button
                onClick={() => { setSuccess(false); setEmail(''); setDebugToken(''); }}
                className="text-[#00D3A8] hover:text-[#00C7A1] text-sm font-medium mb-4 block mx-auto"
              >
                Renvoyer l'email
              </button>

              <Link 
                to="/login"
                className="w-full inline-block text-center text-white font-medium py-3 px-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:opacity-95"
                style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
              >
                Se connecter
              </Link>
            </div>
          ) : (
            // Form State
            <>
              <h2 className="text-xl font-bold text-slate-800 mb-2 text-center">
                Mot de passe oublié ?
              </h2>
              <p className="text-slate-500 text-sm text-center mb-6">
                Veuillez saisir votre adresse email pour recevoir un lien de réinitialisation.
              </p>

              {error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm" data-testid="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2" htmlFor="email">
                    Adresse email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/30 focus:border-[#00D3A8] transition-all placeholder:text-slate-400"
                      placeholder="votre@email.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full text-white font-medium py-3 px-4 rounded-full transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:opacity-95"
                  style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <span>Réinitialiser mon mot de passe</span>
                  )}
                </button>
              </form>

              <div className="mt-5 text-center">
                <Link 
                  to="/login"
                  className="text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors inline-flex items-center space-x-1"
                >
                  <ArrowLeft size={16} />
                  <span>Retour à la connexion</span>
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-white/50 text-xs mt-8">
          © 2026 SyndicAppli - Tous droits réservés
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
